import { FC, useState } from 'react';
import cls from './page-header.module.scss';
import { Link } from 'react-router-dom';
import {
  useCachedSelectedProject,
  useCachedUserType,
  useTranslation,
} from 'hooks';
import { Banner } from 'components/banner/banner';
import { ReportProgressBar } from 'components/report-progress-bar/report-progress-bar';
import Button from 'shared/ui/button/button';
import { track } from 'utils/mixpanel';
import { useReportProgress } from 'shared/hooks/useReportProgress';
import { UserMenu } from 'components/user-menu/user-menu';
import { useAppSelector } from 'store/hooks';
import { ICONS } from 'shared/constants/images';
import { Icon } from 'shared/ui/icon/icon';
import { ExplainMyResultsModal } from './explain-my-results-modal/explain-my-results-modal';

interface PageHeaderProps {
  pageTitle?: string;
  hideBanner?: boolean;
  stateVariant?: 'default' | 'unavailable';
}

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

export const PageHeader: FC<PageHeaderProps> = (props: PageHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { pageTitle, hideBanner = false, stateVariant = 'default' } = props;
  const { t } = useTranslation();
  const { isTrial, isError, isTrialExpired, daysLeft } =
    useCachedSelectedProject();
  const { isKa, userType } = useCachedUserType();
  const progress = useReportProgress();
  const historyMode = useAppSelector(state => state.history.historyMode);

  const handleExplainMyResultsOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleExplainMyResultsCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={cls.pageHeader}>
      <div className={cls.topBar}>
        <h2 className={cls.title}>{pageTitle ?? t('dashboard')}</h2>
        <div className={`${cls.trial} ${isTrialExpired ? cls.expired : ''}`}>
          {userType && !isKa && (
            <Button
              theme='outlined'
              tone='default'
              onClick={handleExplainMyResultsOpenModal}
            >
              <Icon pathToIcon={ICONS.calendar} />
              {t('explainMyResults')}
            </Button>
          )}
          {stateVariant === 'unavailable' ? (
            <>
              <div className={cls.statusCopy}>
                <p className={cls.statusLabel}>{t('terminated')}</p>
                <span className={cls.statusDivider}>•</span>
                <p className={cls.statusText}>{t('reportsUnavailable')}</p>
              </div>
              <Link
                to={isStandalone ? '/subscriptions' : '/report/payment'}
                onClick={() =>
                  track('AIS:CORE_PLAN-MANAGE_BTN-CLICKED', {
                    route: window.location.pathname,
                  })
                }
                className={cls.manage}
              >
                {isStandalone ? t('manageSubscriptions') : t('manage')}
              </Link>
            </>
          ) : isTrial && daysLeft !== null ? (
            <>
              {isTrialExpired ? (
                <p>
                  {t('trial')} {t('expired')}
                </p>
              ) : (
                <p>
                  {t('trial')}, {daysLeft} {t('days_left')}
                </p>
              )}
              <Link
                to={isStandalone ? '/subscriptions' : '/report/payment'}
                onClick={() =>
                  track('AIS:TRIAL_COUNTDOWN-UPGRADE_BTN-CLICKED', {
                    route: window.location.pathname,
                  })
                }
                className={cls.btn}
              >
                {t('upgrade')}
              </Link>
            </>
          ) : (
            <>
              <p>{t('corePlan')}</p>
              <Link
                to={isStandalone ? '/subscriptions' : '/report/payment'}
                onClick={() =>
                  track('AIS:CORE_PLAN-MANAGE_BTN-CLICKED', {
                    route: window.location.pathname,
                  })
                }
                className={cls.manage}
              >
                {isStandalone ? t('manageSubscriptions') : t('manage')}
              </Link>
            </>
          )}
          {isStandalone && <UserMenu />}
        </div>
      </div>
      {!hideBanner && (
        <div className={cls.bannerSection}>
          {historyMode ? (
            <Banner />
          ) : isError ? (
            <div className={cls.errorBanner}>
              <p>{t('reportGenFailedBannerText')}</p>
              <Button>{t('regenerateReport')}</Button>
            </div>
          ) : progress.isVisible ? (
            <ReportProgressBar progress={progress} />
          ) : (
            <Banner />
          )}
        </div>
      )}
      {isModalOpen && (
        <ExplainMyResultsModal
          isOpen={isModalOpen}
          onClose={handleExplainMyResultsCloseModal}
        />
      )}
    </div>
  );
};
